/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, useEffect} from 'react'
import {Dispatch} from 'redux'
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {AuthPage, Logout} from '../modules/auth'
import {RootState} from '../../setup'
import {App} from '../App'
import {Login} from '../modules/auth/components/Login'
import {getConfiguration} from '../../setup/redux/configurations/actions'
import {TermsPage} from '../pages/Terms'
import {PrivacyPage} from '../pages/Privacy'
import {CompanySelect} from '../modules/auth/components/CompanySelect'
import ProtectedRoute from './ProtectedRoute'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const dispatch: Dispatch<any> = useDispatch()
  const {config}: any = useSelector<RootState>(
    (state) => ({
      config: state.configuration.configurations,
    }),
    shallowEqual
  )

  useEffect(() => {
    if (!config) {
      dispatch(getConfiguration())
    }
    const faviconEl = document.getElementById('faviconEl')
    if (faviconEl) {
      if (config && config?.favicon) {
        // @ts-ignore
        faviconEl.href = config?.favicon
      }
    }
    const splashScreenEl = document.getElementById('splashScreenEl')
    if (splashScreenEl) {
      if (config && config.old_logo) {
        // @ts-ignore
        splashScreenEl.src = config?.logo
      }
    }
  }, [config])

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='login' element={<Login />} />
          <Route path='company-select' element={<CompanySelect />} />
          <Route path='logout' element={<Logout />} />
          <Route path='terms' element={<TermsPage />} />
          <Route path='privacy-policy' element={<PrivacyPage />} />
          {/* Error Handling */}
          <Route path='error/*' element={<ErrorsPage />} />

          {/* Auth Routes */}
          <Route path='auth/*' element={<AuthPage />} />

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route path='/*' element={<PrivateRoutes />} />
          </Route>
          <Route index element={<Navigate to='/dashboard' replace />} />

          {/* Catch-All Route */}
          <Route path='*' element={<Navigate to='/error/404' replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
